import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, ExternalLink, Globe, Mail, MapPin, Navigation, Phone, Share2 } from 'lucide-react';
import { Card, PrimaryButton, TicketingShell } from '../../components/ticketing/TicketingShell';
import ShareModal from '../../components/ticketing/ShareModal';
import Linkify from '../../components/ticketing/Linkify';
import { TYPE_LABEL, formatEventDate, inviteSearch, isVideo, paragraphs, writeCart } from '../../utils/ticketing';

const TYPE_STYLE = {
  FREE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAID: 'bg-amber-50 text-amber-800 ring-amber-200',
  APPROVAL: 'bg-sky-50 text-sky-700 ring-sky-200',
};

const accessTone = (value) => (/no\s*access|^-$|none/i.test(value || '') ? 'text-red-600' : 'text-gray-700');

// Wide banners fill the width; taller artwork is capped and shown whole rather than cropped.
const BANNER_CLASS = 'mx-auto block h-auto max-h-[240px] w-full rounded-xl object-contain sm:max-h-[360px] lg:max-h-[420px]';

const Banner = ({ url, alt }) => {
  if (!url) return null;
  return isVideo(url) ? (
    <video src={url} autoPlay muted loop playsInline className={BANNER_CLASS} aria-label={alt} />
  ) : (
    <img src={url} alt={alt} className={BANNER_CLASS} />
  );
};

// A pasted Google Maps embed link is used as is; otherwise the keyless embed searches the venue.
export const mapEmbedUrl = (mapUrl, venue) => (/^https:\/\/(www\.)?google\.[a-z.]+\/maps\/embed/.test(mapUrl || '')
  ? mapUrl
  : `https://maps.google.com/maps?q=${encodeURIComponent(venue)}&z=16&output=embed`);

export const mapLinkUrl = (mapUrl, venue) => (mapUrl && !/\/maps\/embed/.test(mapUrl)
  ? mapUrl
  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`);

const DateBlock = ({ label, value }) => {
  const parts = formatEventDate(value);
  if (!parts) return null;
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-semibold leading-tight">{parts.date}</p>
      <p className="text-xs text-gray-500">{parts.time}</p>
    </div>
  );
};

/** Event landing for premium registration: banner, dates, venue, pass groups and information tabs. */
const TicketingLanding = ({ view, eventRef }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const t = view.ticketing;
  const groups = useMemo(() => t.groups.filter((group) => t.passes.some((pass) => pass.groupId === group.id)), [t]);
  const [selected, setSelected] = useState(groups[0]?.id || null);
  const [tab, setTab] = useState(0);
  const [sharing, setSharing] = useState(false);
  const shareUrl = `${window.location.origin}/register/${eventRef}`;
  const venue = [t.venueName || view.location, t.venueAddress].filter(Boolean).join(', ');

  const register = () => {
    const passes = t.passes.filter((pass) => pass.groupId === selected);
    if (passes.length === 1 && passes[0].maxPerBooking === 1) {
      writeCart(view.eventId, { passId: passes[0].id, qty: 1 });
      navigate(`/register/${eventRef}/book/${passes[0].id}${inviteSearch(search)}`);
    } else {
      navigate(`/register/${eventRef}/passes/${selected}${inviteSearch(search)}`);
    }
  };

  return (
    <TicketingShell view={view}>
      <div className="space-y-6">
        <Card className="p-4 sm:p-6">
          <Banner url={t.bannerUrl} alt={view.name} />
          <div className="mt-5 flex items-start justify-between gap-4">
            <h1 className="text-2xl font-bold sm:text-3xl">{view.name}</h1>
            <button type="button" onClick={() => setSharing(true)} className="rounded-full bg-gray-100 p-2.5 hover:bg-gray-200" aria-label="Share this event">
              <Share2 className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {(view.eventDate || view.eventEndDate) && (
              <div className="flex items-center gap-4 rounded-xl bg-[color-mix(in_srgb,var(--tk-primary)_6%,white)] p-4">
                <CalendarDays className="h-10 w-10 shrink-0 text-[var(--tk-primary)]" />
                <div className="flex flex-1 flex-wrap gap-x-8 gap-y-2">
                  <DateBlock label="Start date" value={view.eventDate} />
                  <DateBlock label="End date" value={view.eventEndDate} />
                </div>
              </div>
            )}
            {(t.venueName || view.location) && (
              <div className="flex items-center gap-4 rounded-xl bg-[color-mix(in_srgb,var(--tk-primary)_6%,white)] p-4">
                <MapPin className="h-10 w-10 shrink-0 text-[var(--tk-primary)]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-semibold">{t.venueName || view.location}</p>
                  {t.venueAddress && <p className="truncate text-sm text-gray-500">{t.venueAddress}</p>}
                </div>
                {t.mapUrl && (
                  <a href={t.mapUrl} target="_blank" rel="noreferrer" className="rounded-lg border p-2 hover:bg-white" aria-label="Open in maps">
                    <Navigation className="h-5 w-5" />
                  </a>
                )}
              </div>
            )}
          </div>
        </Card>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="space-y-6">
            <Card className="p-5 sm:p-7">
              <h2 className="text-xl font-bold">Registration</h2>
              {t.intro && <p className="mt-1 text-gray-600">{t.intro}</p>}
              {groups.length === 0 ? (
                <p className="mt-6 rounded-xl bg-gray-50 p-4 text-sm text-gray-600">Registration opens soon.</p>
              ) : (
                <div role="radiogroup" aria-label="Pass" className="mt-5 space-y-3">
                  {groups.map((group) => {
                    const active = selected === group.id;
                    return (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={active}
                        key={group.id}
                        onClick={() => setSelected(group.id)}
                        className={`w-full rounded-xl border-2 p-4 text-left transition ${active ? 'border-[var(--tk-primary)] bg-[color-mix(in_srgb,var(--tk-primary)_5%,white)]' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${active ? 'border-[var(--tk-primary)]' : 'border-gray-300'}`}>
                            {active && <span className="h-2.5 w-2.5 rounded-full bg-[var(--tk-primary)]" />}
                          </span>
                          {group.iconUrl && <img src={group.iconUrl} alt="" className="h-10 w-10 rounded-full object-cover" />}
                          <span className="flex-1 text-lg font-semibold">{group.name}</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${TYPE_STYLE[group.type] || TYPE_STYLE.PAID}`}>
                            {TYPE_LABEL[group.type] || group.type}
                          </span>
                        </div>
                        {group.description && <p className="mt-2 pl-8 text-sm text-gray-600">{group.description}</p>}
                        {t.days.length > 0 && group.dayAccess.length > 0 && (
                          <div className="mt-3 grid grid-cols-2 gap-2 pl-8 sm:grid-cols-4">
                            {t.days.map((day, i) => (
                              <div key={day} className="rounded-lg bg-white px-3 py-2 ring-1 ring-gray-100">
                                <p className="text-xs font-medium text-gray-500">{day}</p>
                                <p className={`text-sm font-semibold ${accessTone(group.dayAccess[i])}`}>{group.dayAccess[i] || '—'}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {groups.length > 0 && (
                <PrimaryButton onClick={register} disabled={!selected} className="mt-6 w-full text-lg">Register</PrimaryButton>
              )}
            </Card>

            {t.tabs.length > 0 && (
              <Card>
                <div role="tablist" className="flex gap-1 overflow-x-auto border-b px-3 sm:px-5">
                  {t.tabs.map((item, i) => (
                    <button
                      key={item.title}
                      type="button"
                      role="tab"
                      aria-selected={tab === i}
                      onClick={() => setTab(i)}
                      className={`whitespace-nowrap border-b-[3px] px-3 py-4 text-base font-semibold transition ${tab === i ? 'border-[var(--tk-primary)] text-[var(--tk-primary)]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                    >
                      {item.title}
                    </button>
                  ))}
                </div>
                <div role="tabpanel" className="space-y-3 p-5 leading-relaxed text-gray-700 sm:p-7">
                  {paragraphs(t.tabs[tab]?.content).map((para, i) => (
                    <p key={i} className="whitespace-pre-line"><Linkify text={para} /></p>
                  ))}
                </div>
              </Card>
            )}
          </div>

          <aside className="space-y-6">
            {(t.contactEmail || t.contactPhone || t.websiteUrl) && (
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-bold">Contact us</h2>
                <ul className="space-y-3 text-sm">
                  {t.websiteUrl && (
                    <li className="flex items-center gap-3"><Globe className="h-5 w-5 text-gray-400" />
                      <a href={t.websiteUrl} target="_blank" rel="noreferrer" className="truncate text-blue-600 hover:underline">{t.websiteUrl.replace(/^https?:\/\//, '')}</a>
                    </li>
                  )}
                  {t.contactEmail && (
                    <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-gray-400" />
                      <a href={`mailto:${t.contactEmail}`} className="truncate text-blue-600 hover:underline">{t.contactEmail}</a>
                    </li>
                  )}
                  {t.contactPhone && (
                    <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-gray-400" />
                      <a href={`tel:${t.contactPhone}`} className="text-blue-600 hover:underline">{t.contactPhone}</a>
                    </li>
                  )}
                </ul>
              </Card>
            )}
            {(t.venueName || view.location) && (
              <Card>
                <h2 className="flex items-center gap-3 border-b px-6 py-5 text-lg font-bold">
                  <MapPin className="h-6 w-6 text-[var(--tk-primary)]" aria-hidden="true" />Location
                </h2>
                <div className="p-6">
                  <div className="relative overflow-hidden rounded-xl bg-gray-100">
                    <iframe
                      title={`Map of ${venue}`}
                      src={mapEmbedUrl(t.mapUrl, venue)}
                      className="block h-56 w-full border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    <a href={mapLinkUrl(t.mapUrl, venue)} target="_blank" rel="noreferrer"
                      className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded bg-white px-3 py-2 text-sm font-medium text-blue-600 shadow">
                      Maps <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">{venue}</p>
                </div>
              </Card>
            )}
          </aside>
        </div>
      </div>
      <ShareModal open={sharing} onClose={() => setSharing(false)} url={shareUrl} title={view.name} />
    </TicketingShell>
  );
};

export default TicketingLanding;
